/**
 * debugLogger.ts -- Structured debug logger for the debug recorder system.
 *
 * Parallel to logBuffer.ts (which captures raw console output), this logger
 * captures structured, categorized, deduplicated events across JS, GPU, and
 * WASM environments.
 *
 * Performance: when disabled (`_enabled = false`), every public method
 * short-circuits on a single boolean check -- near-zero overhead.
 *
 * React integration: subscribe/getSnapshot for useSyncExternalStore.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type LogCategory = "js" | "gpu" | "wasm";
export type LogSeverity = "trace" | "info" | "warn" | "error";

export interface DebugLogEntry {
  id: number;
  ts: number;
  cat: LogCategory;
  sev: LogSeverity;
  /** Hierarchical tag, e.g. "edit.subdivide", "wasm.unpack", "sculpt.stroke.end" */
  tag: string;
  msg: string;
  data?: Record<string, unknown>;
  /** Dedup count -- 1 = first occurrence, >1 = repeated within dedup window */
  count: number;
}

// ---------------------------------------------------------------------------
// Enable gate
// ---------------------------------------------------------------------------

let _enabled = false;

export function setEnabled(v: boolean): void {
  _enabled = v;
  if (v) startGpuPolling();
  else stopGpuPolling();
}

export function isEnabled(): boolean {
  return _enabled;
}

// ---------------------------------------------------------------------------
// Ring buffer
// ---------------------------------------------------------------------------

const MAX_ENTRIES = 2000;

let entries: DebugLogEntry[] = [];
let nextId = 1;

// ---------------------------------------------------------------------------
// Subscriber management (useSyncExternalStore)
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const fn of listeners) fn();
}

export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

let snapshot: readonly DebugLogEntry[] = entries;

export function getSnapshot(): readonly DebugLogEntry[] {
  return snapshot;
}

export function clearAll(): void {
  entries = [];
  snapshot = entries;
  notify();
}

// ---------------------------------------------------------------------------
// Deduplication
// ---------------------------------------------------------------------------

/** Window (ms) within which identical (tag+msg) entries are collapsed. */
const DEDUP_WINDOW_MS = 500;

interface DedupSlot {
  entry: DebugLogEntry;
  ts: number;
}

const dedupMap = new Map<string, DedupSlot>();

function dedupKey(tag: string, msg: string): string {
  return tag + "\0" + msg;
}

// ---------------------------------------------------------------------------
// Core push
// ---------------------------------------------------------------------------

function push(
  cat: LogCategory,
  sev: LogSeverity,
  tag: string,
  msg: string,
  data?: Record<string, unknown>,
): void {
  const now = Date.now();
  const dk = dedupKey(tag, msg);
  const existing = dedupMap.get(dk);

  if (existing && now - existing.ts < DEDUP_WINDOW_MS) {
    // Deduplicate: increment count on existing entry, update timestamp
    existing.entry.count++;
    existing.ts = now;
    // Merge data if provided (latest values win)
    if (data && existing.entry.data) {
      Object.assign(existing.entry.data, data);
    } else if (data) {
      existing.entry.data = data;
    }
    snapshot = [...entries];
    notify();
    return;
  }

  const entry: DebugLogEntry = {
    id: nextId++,
    ts: now,
    cat,
    sev,
    tag,
    msg,
    data,
    count: 1,
  };

  if (entries.length >= MAX_ENTRIES) {
    entries = entries.slice(entries.length - MAX_ENTRIES + 1);
  }
  entries.push(entry);
  snapshot = entries;

  dedupMap.set(dk, { entry, ts: now });

  // Prune stale dedup slots periodically
  if (dedupMap.size > 200) {
    for (const [k, slot] of dedupMap) {
      if (now - slot.ts > DEDUP_WINDOW_MS * 4) dedupMap.delete(k);
    }
  }

  notify();
}

// ---------------------------------------------------------------------------
// Dab accumulator (for high-frequency sculpt/sketch events)
// ---------------------------------------------------------------------------

interface DabAccum {
  count: number;
  pressureSum: number;
  pressureMin: number;
  pressureMax: number;
  firstTs: number;
  lastData?: Record<string, unknown>;
}

const dabAccums = new Map<string, DabAccum>();

function dabAccumKey(cat: LogCategory, tag: string): string {
  return cat + "\0" + tag;
}

function dabAccumulate(
  cat: LogCategory,
  tag: string,
  data?: Record<string, unknown>,
): void {
  const key = dabAccumKey(cat, tag);
  const existing = dabAccums.get(key);
  const pressure = (data?.pressure as number) ?? 0;

  if (existing) {
    existing.count++;
    existing.pressureSum += pressure;
    existing.pressureMin = Math.min(existing.pressureMin, pressure);
    existing.pressureMax = Math.max(existing.pressureMax, pressure);
    existing.lastData = data;
  } else {
    dabAccums.set(key, {
      count: 1,
      pressureSum: pressure,
      pressureMin: pressure,
      pressureMax: pressure,
      firstTs: Date.now(),
      lastData: data,
    });
  }
}

function dabFlush(
  cat: LogCategory,
  tag: string,
  extraMsg?: string,
  extraData?: Record<string, unknown>,
): void {
  const key = dabAccumKey(cat, tag);
  const accum = dabAccums.get(key);
  dabAccums.delete(key);

  if (!accum) return;

  const avgPressure = accum.count > 0 ? accum.pressureSum / accum.count : 0;
  const durationMs = Date.now() - accum.firstTs;

  const msg =
    `${accum.count} dabs over ${durationMs}ms, avg pressure ${avgPressure.toFixed(2)}` +
    (extraMsg ? ` | ${extraMsg}` : "");

  const data: Record<string, unknown> = {
    dabCount: accum.count,
    durationMs,
    avgPressure: +avgPressure.toFixed(3),
    pressureRange: [
      +accum.pressureMin.toFixed(3),
      +accum.pressureMax.toFixed(3),
    ],
    ...extraData,
  };

  push(cat, "info", tag + ".summary", msg, data);
}

// ---------------------------------------------------------------------------
// GPU frame stats polling
// ---------------------------------------------------------------------------

let gpuPollId: ReturnType<typeof setInterval> | null = null;

function startGpuPolling(): void {
  if (gpuPollId != null) return;
  gpuPollId = setInterval(() => {
    if (!_enabled) return;
    const stats = window.__poseref_frameStats;
    if (!stats) return;
    push(
      "gpu",
      "trace",
      "gpu.frame",
      `${stats.fps.toFixed(0)}fps ${stats.frameTime.toFixed(1)}ms draw:${stats.drawCalls} tri:${stats.triangles}`,
      {
        fps: +stats.fps.toFixed(1),
        frameTimeMs: +stats.frameTime.toFixed(1),
        cpuFrameTimeMs: +stats.cpuFrameTime.toFixed(2),
        drawCalls: stats.drawCalls,
        triangles: stats.triangles,
        geometries: stats.geometries,
        textures: stats.textures,
        jsHeapUsed: stats.jsHeapUsed,
      },
    );
  }, 2000);
}

function stopGpuPolling(): void {
  if (gpuPollId != null) {
    clearInterval(gpuPollId);
    gpuPollId = null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const dlog = {
  trace(
    cat: LogCategory,
    tag: string,
    msg: string,
    data?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    push(cat, "trace", tag, msg, data);
  },
  info(
    cat: LogCategory,
    tag: string,
    msg: string,
    data?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    push(cat, "info", tag, msg, data);
  },
  warn(
    cat: LogCategory,
    tag: string,
    msg: string,
    data?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    push(cat, "warn", tag, msg, data);
  },
  error(
    cat: LogCategory,
    tag: string,
    msg: string,
    data?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    push(cat, "error", tag, msg, data);
  },
  /** Accumulate a high-frequency event (sculpt/sketch dab) without emitting. */
  dabAccum(
    cat: LogCategory,
    tag: string,
    data?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    dabAccumulate(cat, tag, data);
  },
  /** Flush accumulated dabs as a single summary entry. */
  dabFlush(
    cat: LogCategory,
    tag: string,
    extraMsg?: string,
    extraData?: Record<string, unknown>,
  ): void {
    if (!_enabled) return;
    dabFlush(cat, tag, extraMsg, extraData);
  },
};
